import { doc, getDoc, collection, query, getDocs } from "firebase/firestore";
import { getDb } from "./index.js";
import { ClientConfigSchema, type ClientConfig } from "../schema/clientConfig.js";
import {
  SiteConfigSchema,
  type SiteConfig,
  FOOD_BEV_HOME_SECTIONS,
  FOOD_BEV_STARTER_ROUTES,
} from "../schema/siteConfig.js";

/**
 * Resolves the clientId for the current hostname.
 *
 * Resolution order:
 * 1. ?clientId= query param (dev/preview override)
 * 2. VITE_CLIENT_ID env var (CI / per-build staging)
 * 3. Firestore domains/{hostname} lookup (production)
 * 4. "demo-foodbev" fallback in dev
 */
export async function resolveClientId(): Promise<string> {
  const params = new URLSearchParams(window.location.search);
  const paramId = params.get("clientId");
  if (paramId) return paramId;

  const envId = import.meta.env.VITE_CLIENT_ID;
  if (envId) return envId as string;

  const hostname = window.location.hostname;
  if (hostname !== "localhost" && hostname !== "127.0.0.1") {
    try {
      const snap = await getDoc(doc(getDb(), "domains", hostname));
      if (snap.exists()) {
        const data = snap.data() as { clientId?: string };
        if (data.clientId) return data.clientId;
      }
    } catch {
      // Fall through to dev fallback
    }
  }

  return import.meta.env.DEV ? "demo-foodbev" : "unknown";
}

const DEMO_SITE_FALLBACK = {
  sections: FOOD_BEV_HOME_SECTIONS,
  routes: FOOD_BEV_STARTER_ROUTES,
} as const;

/**
 * Fetches and validates the core ClientConfig document.
 * Falls back to a minimal demo config when running without seeded Firestore data
 * (Phase 1 dev convenience — remove for production).
 */
export async function fetchClientConfig(clientId: string): Promise<ClientConfig> {
  try {
    const snap = await getDoc(doc(getDb(), "clients", clientId));
    if (!snap.exists()) {
      return ClientConfigSchema.parse({
        clientId,
        businessName: "Demo Restaurant",
        vertical: "food-bev",
        layoutType: "A",
        status: "trial",
        features: { reservations: true, online_ordering: false },
      });
    }
    return ClientConfigSchema.parse({ ...snap.data(), clientId });
  } catch {
    // Firebase unreachable (emulator not running, offline dev) — use demo config
    return ClientConfigSchema.parse({
      clientId,
      businessName: "Demo Restaurant",
      vertical: "food-bev",
      layoutType: "A",
      status: "trial",
      features: { reservations: true, online_ordering: false },
    });
  }
}

/**
 * Fetches and validates the SiteConfig document.
 * Falls back to the food-bev starter preset on missing/invalid data.
 */
export async function fetchSiteConfig(clientId: string): Promise<SiteConfig> {
  try {
    const snap = await getDoc(doc(getDb(), "clients", clientId, "config", "site"));
    if (!snap.exists()) {
      return SiteConfigSchema.parse({ clientId, ...DEMO_SITE_FALLBACK });
    }
    const parsed = SiteConfigSchema.safeParse({ ...snap.data(), clientId });
    if (!parsed.success) {
      console.error("[siteConfig] Validation failed, using starter preset:", parsed.error);
      return SiteConfigSchema.parse({ clientId, ...DEMO_SITE_FALLBACK });
    }
    return parsed.data;
  } catch {
    // Firebase unreachable — use starter preset so the app still renders
    return SiteConfigSchema.parse({ clientId, ...DEMO_SITE_FALLBACK });
  }
}

// Re-export Firestore primitives so apps don't need a direct firebase dependency
export { doc, getDoc, collection, query, getDocs };
